import { pipeState } from '../utils';
import {
  CorrelationId,
  DEFAULT_SCOPE_NAME,
  EntityAdapterOptions,
  EntityDataAdapter,
  EntityMetaAdapter,
  EntityState,
  GenericEntity,
  GenericIdSelector,
  GenericMetaOptions,
  ID,
  OptimisticUpdate,
  PrependingOption,
  Scope,
} from './types';
import { resolveScope } from './utils';

/* eslint-disable max-lines */

export const createHydratedEntityState = <
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
>(
  initialState: EntityState<Entity, MetaOptions>,
  entities: Record<string, Entity[]> | Entity[],
  adapter: EntityDataAdapter<Entity, MetaOptions> & EntityMetaAdapter<Entity, MetaOptions>,
): EntityState<Entity> => {
  if (Array.isArray(entities)) {
    return adapter.setMany(initialState, entities, { scope: DEFAULT_SCOPE_NAME });
  } else {
    const transforms = Object.entries(entities).map(
      ([scope, entities]) =>
        (state: EntityState<Entity>) =>
          adapter.setMany(state, entities, { scope }),
    );

    return pipeState(initialState, ...transforms);
  }
};

// eslint-disable-next-line max-lines-per-function
export const getEntityDataAdapter = <
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
>(
  selectId: GenericIdSelector<any>,
  initialState: EntityState<Entity>,
) => {
  const adapter: EntityDataAdapter<Entity, MetaOptions> = {
    addOne: (
      state: EntityState<Entity>,
      data: Entity,
      options: EntityAdapterOptions & CorrelationId & PrependingOption = {},
    ): EntityState<Entity> => {
      const scopeName = resolveScope(options.scope);
      const scopeData = state.scope[scopeName] || [];
      const entityId = options.correlationId || selectId(data);

      const prependOption = options.prepend;

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [entityId]: data,
        },
        scope: {
          ...state.scope,
          [scopeName]: [
            ...new Set(prependOption ? [entityId, ...scopeData] : [...scopeData, entityId]),
          ],
        },
      };
    },
    addMany: (
      state: EntityState<Entity>,
      data: Entity[],
      options: EntityAdapterOptions & PrependingOption = {},
    ): EntityState<Entity> => {
      const scopeName = resolveScope(options.scope);
      const scopeData = state.scope[scopeName] || [];

      const prependOption = options.prepend;

      function appendMany() {
        return [...new Set([...scopeData, ...data.map(selectId)])];
      }

      function prependMany() {
        return [...new Set([...data.map(selectId), ...scopeData])];
      }

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          ...Object.fromEntries(data.map(item => [selectId(item), item])),
        },
        scope: {
          ...state.scope,
          [scopeName]: prependOption ? prependMany() : appendMany(),
        },
      };
    },
    setOne: (
      state: EntityState<Entity>,
      data: Entity,
      options: EntityAdapterOptions = {},
    ): EntityState<Entity> => {
      const scopeName = resolveScope(options.scope);
      const id = selectId(data);

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          [id]: data,
        },
        scope: {
          ...state.scope,
          [scopeName]: [id],
        },
      };
    },
    setMany: (
      state: EntityState<Entity>,
      data: Entity[],
      options: EntityAdapterOptions = {},
    ): EntityState<Entity> => {
      const scopeName = resolveScope(options.scope);

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          ...Object.fromEntries(data.map(item => [selectId(item), item])),
        },
        scope: {
          ...state.scope,
          [scopeName]: data.map(selectId),
        },
      };
    },
    writeOne: (state: EntityState<Entity>, data: Entity): EntityState<Entity> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        [selectId(data)]: data,
      },
    }),
    writeMany: (state: EntityState<Entity>, data: Entity[]): EntityState<Entity> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        ...Object.fromEntries(data.map(item => [selectId(item), item])),
      },
    }),
    replaceOne: (
      state: EntityState<Entity>,
      previousId: ID,
      current: Entity,
    ): EntityState<Entity> => {
      const { [previousId]: _, ...entityMap } = state.entityMap;
      const { [previousId]: previousEntityMeta, ...entityMetaMap } = state.entityMetaMap;

      const currentId = selectId(current);

      return {
        ...state,
        entityMap: {
          ...entityMap,
          [currentId]: current,
        },
        entityMetaMap: {
          ...entityMetaMap,
          [currentId]: previousEntityMeta,
        },
        scope: Object.fromEntries(
          Object.entries(state.scope).map(([scopeName, ids]) => [
            scopeName,
            ids.map(id => (id === previousId ? currentId : id)),
          ]),
        ),
      };
    },
    replaceMany: (
      state: EntityState<Entity>,
      data: Array<{ previousId: ID; current: Entity }>,
    ): EntityState<Entity> => {
      const replacementMap = Object.fromEntries(data.map(item => [item.previousId, item]));
      const replace = <O extends Record<string | number, any>>(
        object: O,
        replacer: (key: keyof O, value: O[keyof O]) => [keyof O, O[keyof O]],
      ) => Object.fromEntries(Object.entries(object).map(([key, value]) => replacer(key, value)));

      return {
        ...state,
        entityMap: replace(state.entityMap, (id, entity) => {
          const replacement = replacementMap[id];
          return replacement ? [selectId(replacement.current), replacement.current] : [id, entity];
        }),
        entityMetaMap: replace(state.entityMetaMap, (id, meta) => {
          const replacement = replacementMap[id];
          return replacement ? [selectId(replacement.current), meta] : [id, meta];
        }),
        scope: replace(state.scope, (scopeName, ids) => [
          scopeName,
          ids.map(id => (replacementMap[id] ? selectId(replacementMap[id].current) : id)),
        ]),
      };
    },
    removeOne: (state: EntityState<Entity>, id: ID): EntityState<Entity> =>
      adapter.removeMany(state, [id]),
    removeMany: (state: EntityState<Entity>, idsToRemove: ID[]): EntityState<Entity> => {
      const stringIds = idsToRemove.map(id => `${id}`);
      const scope = Object.entries(state.scope).reduce(
        (acc, [scopeName, ids]) => {
          acc[scopeName] = ids.filter(id => !stringIds.includes(`${id}`));
          return acc;
        },
        <typeof state.scope>{},
      );

      return {
        ...state,
        scope,
        entityMap: Object.fromEntries(
          Object.entries(state.entityMap).filter(([key, entity]) => !stringIds.includes(key)),
        ),
        entityMetaMap: Object.fromEntries(
          Object.entries(state.entityMetaMap).filter(([key, entity]) => !stringIds.includes(key)),
        ),
      };
    },
    updateOne: (state: EntityState<Entity>, data: Entity): EntityState<Entity> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        [selectId(data)]: {
          ...state.entityMap[selectId(data)],
          ...data,
        },
      },
    }),
    updateMany: (state: EntityState<Entity>, data: Entity[]): EntityState<Entity> => ({
      ...state,
      entityMap: {
        ...state.entityMap,
        ...Object.fromEntries(
          data.map(item => [selectId(item), { ...state.entityMap[selectId(item)], ...item }]),
        ),
      },
    }),
    upsertOne: (
      state: EntityState<Entity>,
      data: Entity,
      options: EntityAdapterOptions = {},
    ): EntityState<Entity> => {
      const hasEntity = selectId(data) in state.entityMap;
      return hasEntity ? adapter.updateOne(state, data) : adapter.addOne(state, data, options);
    },
    updateOrCreateMany: (state: EntityState<Entity>, data: Entity[]): EntityState<Entity> => {
      const entityMap = state.entityMap;
      const newEntityMap: Record<ID, Entity | null> = Object.fromEntries(
        data.map(entity => {
          const id = selectId(entity);
          const newEntity = id in entityMap ? { ...entityMap[id], ...entity } : entity;

          return [id, newEntity];
        }),
      );

      return {
        ...state,
        entityMap: {
          ...entityMap,
          ...newEntityMap,
        },
      };
    },
    upsertMany: (
      state: EntityState<Entity>,
      data: Entity[],
      options: EntityAdapterOptions = {},
    ): EntityState<Entity> => {
      const scopeName = resolveScope(options.scope);
      const scopeData = state.scope[scopeName] || [];
      const toUpdate = data.filter(entity => selectId(entity) in state.entityMap);
      const toInsert = data.filter(entity => !(selectId(entity) in state.entityMap));

      const newScope = [
        ...new Set([...scopeData, ...toUpdate.map(selectId), ...toInsert.map(selectId)]),
      ];

      return {
        ...state,
        entityMap: {
          ...state.entityMap,
          ...Object.fromEntries(
            toUpdate.map(item => [selectId(item), { ...state.entityMap[selectId(item)], ...item }]),
          ),
          ...Object.fromEntries(toInsert.map(item => [selectId(item), item])),
        },
        scope: {
          ...state.scope,
          [scopeName]: newScope,
        },
      };
    },
    optimisticUpdateSet: (
      state: EntityState<Entity>,
      correlationId: string,
      data: OptimisticUpdate<Entity>,
    ): EntityState<Entity> => ({
      ...state,
      optimisticUpdate: {
        ...state.optimisticUpdate,
        [correlationId]: data,
      },
    }),
    optimisticUpdateRemove: (
      state: EntityState<Entity>,
      correlationId: string,
    ): EntityState<Entity> => {
      const { [correlationId]: _, ...optimisticUpdate } = state.optimisticUpdate;
      return {
        ...state,
        optimisticUpdate,
      };
    },
    optimisticDeleteSet: (state: EntityState<Entity>, data: Entity): EntityState<Entity> => ({
      ...state,
      optimisticDelete: {
        ...state.optimisticDelete,
        [selectId(data)]: data,
      },
    }),
    optimisticDeleteRemove: (state: EntityState<Entity>, id: Entity['id']): EntityState<Entity> => {
      const { [id]: _, ...optimisticDelete } = state.optimisticDelete;
      return {
        ...state,
        optimisticDelete,
      };
    },
    resetScope: (state: EntityState<Entity>, scope?: Scope) => {
      const scopeName = resolveScope(scope);
      return <EntityState<Entity>>{
        ...state,
        scope: {
          ...state.scope,
          [scopeName]: [],
        },
        scopeMeta: {
          ...state.scopeMeta,
          [scopeName]: {
            loaded: false,
            pending: false,
            failed: false,
            error: null,
          },
        },
        scopePagination: {
          ...state.scopePagination,
          [scopeName]: {
            limit: null,
            page: null,
            totalCount: null,
          },
        },
      };
    },
    resetAll: (state: EntityState<Entity>): EntityState<Entity> => initialState,
  };

  return adapter;
};
